import { EBrowserStorageType } from '@/core/apis/browser_storage_api/browser_storage_api_types';
import { RecacheStorageHandler } from '@/core/data_handlers/storage_handler_cached';
import { Logger } from '@/shared/logger/logger';

export type DestinationStatus = {
  errorDetails?: string;
  isAvailable: boolean;
  lastChecked: number;
};

type DestinationConfig = {
  destinationUrl: string;
  storageNamespace: string;
};

export class DestinationStatusHandler {
  private readonly _config: DestinationConfig;
  private readonly _storageHandler: RecacheStorageHandler<DestinationStatus>;

  constructor(config: DestinationConfig) {
    this._config = config;
    this._storageHandler = new RecacheStorageHandler<DestinationStatus>(
      this._config.storageNamespace,
      {
        isAvailable: false,
        lastChecked: 0,
      },
      EBrowserStorageType.LOCALSTORAGE,
      2 * 60 * 1000, // 2 minutes
      this.checkDestinationAvailability.bind(this),
    );
  }
  private async checkDestinationAvailability(): Promise<DestinationStatus> {
    const startTime = Date.now();
    const controller = new AbortController();

    // Short timeout since we only need headers
    const timeoutId = setTimeout(() => controller.abort(), 3000);

    try {
      const response = await fetch(this._config.destinationUrl, {
        // Keep request headers minimal
        headers: {
          'Cache-Control': 'no-cache',
        },
        method: 'HEAD', // Only request headers
        mode: 'no-cors', // Keep no-cors mode for compatibility
        signal: controller.signal,
      });

      clearTimeout(timeoutId);
      const responseTime = Date.now() - startTime;

      // For HEAD requests in no-cors:
      // - Success: response.type will be 'opaque' due to no-cors mode
      // - Server doesn't support HEAD: will fail and go to catch block
      const isAvailable =
        response.type === 'opaque' ||
        response.type === 'basic' ||
        response.type === 'cors';

      if (!isAvailable) {
        Logger.warn(
          `Unexpected response type for ${this._config.storageNamespace}:`,
          `Type: ${response.type}`,
          `Time: ${responseTime}ms`,
        );
      }

      return {
        errorDetails: isAvailable
          ? undefined
          : `Invalid response type: ${response.type}`,
        isAvailable,
        lastChecked: Date.now(),
      };
    } catch (error: any) {
      clearTimeout(timeoutId);
      const responseTime = Date.now() - startTime;

      // If HEAD request fails, resource might still be available but not supporting HEAD
      // Log this case specifically for monitoring
      const isHeadMethodError =
        error.message?.includes('method not allowed') ||
        error.message?.toLowerCase().includes('head');

      Logger.error(
        `Availability check failed for ${this._config.storageNamespace}:`,
        `Time: ${responseTime}ms`,
        isHeadMethodError ? 'HEAD method not supported' : error?.message,
      );

      return {
        errorDetails: isHeadMethodError
          ? 'Destination does not support HEAD requests'
          : `Connection failed: ${error?.message || 'Unknown error'}`,
        isAvailable: false,
        lastChecked: Date.now(),
      };
    }
  }
  async getStatus(): Promise<DestinationStatus> {
    return this._storageHandler.getAsync();
  }
}
